import { useCallback, useSyncExternalStore } from "react";

/**
 * Which repository folders are disclosed, keyed `<connectionId>:<path>` — persisted per browser
 * so a tree reopens where it was left, in every host, without borrowing any one host's store.
 * Storage can be absent or hostile (private window, thumbnail capture): every access is guarded
 * and the tree still works in memory.
 */
const KEY = "docit.git.expanded";
type Listener = () => void;

let state: Record<string, boolean> = read();
const listeners = new Set<Listener>();

function read(): Record<string, boolean> {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function write(next: Record<string, boolean>) {
  state = next;
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(next));
  } catch {
    // In memory only; nothing to do.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function repoExpansionKey(connectionId: string, path: string): string {
  return `${connectionId}:${path}`;
}

export function useRepoExpansion() {
  const expanded = useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
  const toggle = useCallback((key: string) => {
    const next = { ...state };
    if (next[key]) delete next[key];
    else next[key] = true;
    write(next);
  }, []);
  return { expanded, toggle };
}
